import React, { useRef, useEffect, useState, useCallback } from "react";
import HomePageBannerItem from "./HomePageBannerItem";
import { pagerContainerClasses, pagerClasses, innerClasses } from "./banner.styles";
import { useHomePageData } from "../utilities";
const HomePageAdBanner = () => {
  const {
    banner
  } = useHomePageData();
  const [activeIndex, setActiveIndex] = useState(0);
  const [translation, setTranslation] = useState(0);
  const [width, setWidth] = useState(0);
  const paused = useRef(false);
  const isDown = useRef(false);
  const startX = useRef<number | undefined>(undefined);
  const endX = useRef<number | undefined>(undefined);
  const preventClick = useRef(false);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const bannerItemsCount = banner.length;
  const advanceIndex = useCallback((offset: number) => {
    setActiveIndex(prev => (prev + offset + bannerItemsCount) % bannerItemsCount);
  }, [bannerItemsCount]);
  useEffect(() => {
    const setSlideInterval = setInterval(() => {
      if (!paused.current) {
        advanceIndex(1);
      }
    }, 7000);
    return () => {
      clearInterval(setSlideInterval);
    };
  }, [advanceIndex]);
  const setPaused = (isPaused: boolean) => {
    paused.current = isPaused;
  };
  useEffect(() => {
    const carouselReference = carouselRef;
    const handleDown = (e: MouseEvent | TouchEvent): void => {
      isDown.current = true;
      if (e instanceof MouseEvent) {
        startX.current = e.x;
      } else if (e instanceof TouchEvent) {
        startX.current = e.changedTouches[0]?.clientX || undefined;
      }
    };
    const handleUp = () => {
      setTranslation(0);
      if (!isDown.current) {
        return;
      }
      isDown.current = false;
      if (startX.current !== undefined && endX.current !== undefined) {
        const distPercent = (endX.current - startX.current) / width * 100;
        if (Math.abs(distPercent) < 5) {
          startX.current = undefined;
          return;
        }
        advanceIndex(Math.sign(distPercent) * -1);
      }
      startX.current = undefined;
      endX.current = undefined;
    };
    const handleClick = (e: MouseEvent | TouchEvent) => {
      if (preventClick.current) {
        e.preventDefault();
      }
      preventClick.current = false;
    };
    const handleMove = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      if (isDown.current) {
        preventClick.current = true;
        if (e instanceof MouseEvent) {
          endX.current = e.x;
        } else if (e instanceof TouchEvent) {
          endX.current = e.changedTouches[0]?.clientX || 0;
        }
        if (startX.current !== undefined && endX.current !== undefined) {
          const diffX = endX.current - startX.current;
          setTranslation(diffX / width * 100);
        }
      }
    };
    if (carouselReference.current) {
      setWidth(carouselReference.current.offsetWidth);
      carouselReference.current.addEventListener("mousedown", handleDown);
      carouselReference.current.addEventListener("touchstart", handleDown);
      carouselReference.current.addEventListener("mouseup", handleUp);
      carouselReference.current.addEventListener("mouseleave", handleUp);
      carouselReference.current.addEventListener("touchend", handleUp);
      carouselReference.current.addEventListener("click", handleClick);
      carouselReference.current.addEventListener("mousemove", handleMove);
      carouselReference.current.addEventListener("touchmove", handleMove);
    }
    return () => {
      if (carouselReference.current) {
        carouselReference.current.removeEventListener("mousedown", handleDown);
        carouselReference.current.removeEventListener("touchstart", handleDown);
        carouselReference.current.removeEventListener("mouseup", handleUp);
        carouselReference.current.removeEventListener("mouseleave", handleUp);
        carouselReference.current.removeEventListener("touchend", handleUp);
        carouselReference.current.removeEventListener("click", handleClick);
        carouselReference.current.removeEventListener("mousemove", handleMove);
        carouselReference.current.removeEventListener("touchmove", handleMove);
      }
    };
  }, [width, advanceIndex]);
  return <section className="lt:px-[15px] lz:px-10 xxl:px-[7%]" data-sentry-component="HomePageAdBanner" data-sentry-source-file="HomePageAdBanner.tsx">
      <div data-testid="banner" ref={carouselRef} aria-label="ad carousel" className="relative overflow-hidden mx-auto lt:max-w-[916px] lg:max-w-[974px] lz:max-w-[1200px] xxl:max-w-[1920px]" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
        <div className="absolute text-center w-full bottom-3 z-[1]">
          <div role="tablist" className={pagerContainerClasses}>
            {banner.map((item, index) => <button key={item.bannerId} data-testid={`tab${index}`} onClick={() => setActiveIndex(index)} type="button" role="tab" aria-label={`Slide ${index + 1}`} aria-selected={index === activeIndex ? "true" : "false"} aria-controls={`tabpanel${index}`} className={`${pagerClasses} banner-pager ${index === activeIndex ? "shadow-[0_0_7px_0_#383838] bg-white/70" : "shadow-[0_0_7px_0_#ffffff] bg-black/60"}`}>
                {index + 1}
              </button>)}
          </div>
        </div>
        <div className={innerClasses} data-testid="innerBanner" style={{
        transform: `translateX(${activeIndex * -100 + translation}%)`
      }}>
          {banner.map((item, index) => <HomePageBannerItem key={item.bannerId} hidden={false} id={`tabpanel${index}`} groupAriaLabel={`Slide ${index + 1} of ${bannerItemsCount}`} bannerItem={item} position={index + 1} />)}
        </div>
      </div>
    </section>;
};
export default HomePageAdBanner;